/**
 * The shadows a planet and its moons throw on each other: per fragment, the
 * share of the Sun each caster covers from that point on the receiver, read
 * from a table of `sunCover.ts`'s own `blockedShare`, taken out of the direct
 * light.
 */

import * as THREE from 'three';

import { blockedShare, CORONA_SHARE } from './sunCover.ts';
import type { WorldVec } from '../world/frame.ts';
import type { BodyRow } from '../world/types';

export const BODY_SHADOW_MAX_CASTERS = 4;

/** Columns: the occulter's edge past the Sun's centre, -1 to 1 Sun radii. Rows: b / (1 + b), b the occulter's radius over the Sun's. */
const LUT_W = 256;
/** Odd, so b = 1 is a row: an occulter the Sun's own size covers it with a square-root edge in b. */
const LUT_H = 129;
/** The last row: an occulter so much wider than the Sun that its edge is straight across it. */
const STRAIGHT_EDGE_B = 1e4;
/** `sol.ts`'s SOLAR_RADIUS_KM; restated so the tile and globe hooks that import this stay clear of the disc's module. */
const SUN_RADIUS_M = 695_700_000;

function rowB(j: number): number {
  const u = j / (LUT_H - 1);
  return u >= 1 ? STRAIGHT_EDGE_B : u / (1 - u);
}

let table: Float32Array | null = null;
function shareTable(): Float32Array {
  if (table) return table;
  const t = new Float32Array(LUT_W * LUT_H);
  for (let j = 0; j < LUT_H; j++) {
    const b = rowB(j);
    for (let i = 0; i < LUT_W; i++) {
      const x = -1 + (2 * i) / (LUT_W - 1);
      t[j * LUT_W + i] = blockedShare(1, b, Math.max(0, b + x));
    }
  }
  table = t;
  return t;
}

/**
 * The share the shader reads for an occulter of `b` Sun radii whose centre is
 * `c` Sun radii off the Sun's: the same bilinear lookup, on the CPU.
 */
export function tableShare(b: number, c: number): number {
  const t = shareTable();
  const x = Math.min(1, Math.max(-1, c - b));
  const fx = ((x + 1) / 2) * (LUT_W - 1);
  const fy = (b / (1 + b)) * (LUT_H - 1);
  const i0 = Math.min(LUT_W - 2, Math.floor(fx));
  const j0 = Math.min(LUT_H - 2, Math.floor(fy));
  const ax = fx - i0;
  const ay = fy - j0;
  const at = (j: number, i: number): number => t[j * LUT_W + i]!;
  return (at(j0, i0) * (1 - ax) + at(j0, i0 + 1) * ax) * (1 - ay)
    + (at(j0 + 1, i0) * (1 - ax) + at(j0 + 1, i0 + 1) * ax) * ay;
}

/** Declared where the hook is on, so the atmosphere chunk scales its in-scatter by `fxBsK` only on those programs. */
const DECL_GLSL = /* glsl */ `
#define FX_BS_SHARE
uniform sampler2D uBsLut;
uniform vec3 uBsSun;
uniform vec4 uBsCasters[${BODY_SHADOW_MAX_CASTERS}];
uniform int uBsCount;
uniform vec2 uBsEye;
uniform float uBsForce;
varying vec3 vBsDir;
float fxBsShare(vec3 p, vec4 c, vec3 toSun, float dSun, float sunR) {
  vec3 v = c.xyz - p;
  float d = length(v);
  if (d <= c.w || d >= dSun) return 0.0;
  float along = dot(v, toSun);
  if (along <= 0.0) return 0.0;
  float b = asin(c.w / d) / sunR;
  float x = atan(length(cross(v, toSun)), along) / sunR - b;
  if (x >= 1.0) return 0.0;
  vec2 uv = vec2((clamp(x, -1.0, 1.0) + 1.0) * 0.5 * ${(LUT_W - 1) / LUT_W} + ${0.5 / LUT_W},
    b / (1.0 + b) * ${(LUT_H - 1) / LUT_H} + ${0.5 / LUT_H});
  return texture2D(uBsLut, uv).r;
}
`;

/**
 * After the lights: the direct light is what a caster takes, the ambient fill is not.
 * `uBsEye` is the share the light rig already carries for the ground under the eye
 * (`sunCover.ground`) and its weight; that much of this fragment's own share is put back.
 */
const INJECT_GLSL = /* glsl */ `
#include <lights_fragment_end>
float fxBsK = 1.0;
{
  float fxBsF = 1.0;
  if (uBsForce >= 0.0) {
    fxBsF = uBsForce;
  } else if (uBsCount > 0 && uBsEye.y < 1.0) {
    vec3 fxBsP = vBsDir;
    vec3 fxBsS = uBsSun - fxBsP;
    float fxBsDs = length(fxBsS);
    vec3 fxBsU = fxBsS / fxBsDs;
    float fxBsSunR = asin(min(1.0, ${SUN_RADIUS_M.toFixed(1)} / fxBsDs));
    float fxBsBlocked = 0.0;
    for (int i = 0; i < ${BODY_SHADOW_MAX_CASTERS}; i++) {
      if (i >= uBsCount) break;
      fxBsBlocked += fxBsShare(fxBsP, uBsCasters[i], fxBsU, fxBsDs, fxBsSunR);
    }
    fxBsF = max(${CORONA_SHARE.toExponential()}, 1.0 - fxBsBlocked);
  }
  fxBsK = mix(fxBsF, uBsEye.x, uBsEye.y) / max(uBsEye.x, ${CORONA_SHARE.toExponential()});
  reflectedLight.directDiffuse *= fxBsK;
  reflectedLight.directSpecular *= fxBsK;
}
`;

type Uniform<T> = { value: T };

/** One receiver's uniforms, shared by reference with its globe and every tile of it. */
export interface BodyShadowRef {
  readonly bodyId: string;
  readonly uniforms: {
    readonly uBsLut: Uniform<THREE.DataTexture>;
    readonly uBsSun: Uniform<THREE.Vector3>;
    readonly uBsCasters: Uniform<THREE.Vector4[]>;
    readonly uBsCount: Uniform<number>;
    readonly uBsEye: Uniform<THREE.Vector2>;
    readonly uBsForce: Uniform<number>;
  };
  /** The casters on it this frame, by id, in uniform order. */
  readonly casterIds: string[];
}

/**
 * The chunk on one program. `origin` is a tile's own centre in the body's
 * frame (its vertices are relative to it); the globe's vertices are already
 * body-fixed and pass none.
 */
export function applyBodyShadow(shader: THREE.WebGLProgramParametersWithUniforms,
  ref: BodyShadowRef, origin: THREE.Vector3 | null = null): void {
  Object.assign(shader.uniforms, ref.uniforms);
  shader.uniforms['uBsOrigin'] = { value: origin ?? ZERO };
  shader.vertexShader = `uniform vec3 uBsOrigin;\nvarying vec3 vBsDir;\n${shader.vertexShader}`
    .replace('#include <begin_vertex>', '#include <begin_vertex>\n  vBsDir = position + uBsOrigin;');
  shader.fragmentShader = `${DECL_GLSL}\n${shader.fragmentShader}`
    .replace('#include <lights_fragment_end>', INJECT_GLSL);
}
const ZERO = new THREE.Vector3();

/** The program cache key's own suffix: three's does not vary with a hook's content. */
export function bodyShadowKey(ref: BodyShadowRef | null): string {
  return ref === null ? '' : '-bsh1';
}

/** Userdata key: which receiver this material is, for every writer of its hook. */
const SHADOW_REF = 'fxcivBodyShadowRef';

export function markBodyShadow(mat: THREE.Material, ref: BodyShadowRef): void {
  mat.userData[SHADOW_REF] = ref;
}

export function bodyShadowRefOf(mat: THREE.Material): BodyShadowRef | null {
  const ref = mat.userData[SHADOW_REF] as BodyShadowRef | undefined;
  return ref ?? null;
}

/** The hook on a globe nothing else writes one for. */
export function applyBodyShadowOnly(material: THREE.Material, ref: BodyShadowRef): void {
  material.onBeforeCompile = (shader): void => applyBodyShadow(shader, ref);
  material.customProgramCacheKey = (): string => `fxciv-globe${bodyShadowKey(ref)}`;
  material.needsUpdate = true;
}

/** What `update` reads off each body: the orrery's own fields. */
export interface BodyShadowBody {
  readonly row: BodyRow;
  readonly world: WorldVec;
  readonly quaternion: THREE.Quaternion;
}

export interface BodyShadowDebug {
  /** Every receiver's direct light held at this share, or null for its own. */
  force: number | null;
  /** `body-shadow-check --inject occulter-offset`: each caster drawn this many of its radii off the Sun line. */
  offsetRadii: number;
}

export interface BodyShadowsHandle {
  /** The receiver's uniforms, or null for a body nothing in its system can shadow. */
  refFor(bodyId: string): BodyShadowRef | null;
  /** Casters and the Sun into each receiver's frame, from this frame's positions. */
  update(bodies: readonly BodyShadowBody[]): void;
  /** The share of the ground's light the light rig already carries, and how much of it is the eye's. */
  setEyeShare(ground: number, weight: number): void;
  readonly debug: BodyShadowDebug;
  dispose(): void;
}

/**
 * Bodies that can stand between the Sun and `row`: what `cameraRig.inLocalSky`
 * lists as `row`'s own sky, so the per-fragment share and `sunCover.measure`
 * add up the same casters.
 */
export function shadowCasters(row: BodyRow, all: readonly BodyRow[]): BodyRow[] {
  return all.filter((o) => {
    if (o.id === row.id || o.orbit === null) return false;
    if (o.parent === row.id) return true;
    if (row.parent === null || row.parent === 'sol') return false;
    return o.id === row.parent || o.parent === row.parent;
  });
}

export function createBodyShadows(rows: readonly BodyRow[]): BodyShadowsHandle {
  const t = shareTable();
  const half = new Uint16Array(t.length);
  for (let i = 0; i < t.length; i++) half[i] = THREE.DataUtils.toHalfFloat(t[i]!);
  const lut = new THREE.DataTexture(half, LUT_W, LUT_H, THREE.RedFormat, THREE.HalfFloatType);
  lut.name = 'body-shadow-share';
  lut.minFilter = lut.magFilter = THREE.LinearFilter;
  lut.wrapS = lut.wrapT = THREE.ClampToEdgeWrapping;
  lut.generateMipmaps = false;
  lut.needsUpdate = true;

  const shared = {
    uBsLut: { value: lut },
    uBsEye: { value: new THREE.Vector2(1, 0) },
    uBsForce: { value: -1 },
  };
  const debug: BodyShadowDebug = { force: null, offsetRadii: 0 };
  const casterRows = new Map<string, readonly BodyRow[]>();
  const refs = new Map<string, BodyShadowRef>();
  for (const r of rows) {
    if (r.orbit === null) continue;
    const casters = shadowCasters(r, rows);
    if (casters.length === 0) continue;
    casterRows.set(r.id, casters);
    refs.set(r.id, {
      bodyId: r.id,
      uniforms: {
        ...shared,
        uBsSun: { value: new THREE.Vector3() },
        uBsCasters: { value: Array.from({ length: BODY_SHADOW_MAX_CASTERS }, () => new THREE.Vector4()) },
        uBsCount: { value: 0 },
      },
      casterIds: [],
    });
  }
  interface Receiver {
    readonly body: BodyShadowBody;
    readonly casters: BodyShadowBody[];
    readonly ref: BodyShadowRef;
  }
  const receivers: Receiver[] = [];
  let boundTo: readonly BodyShadowBody[] | null = null;
  function bind(bodies: readonly BodyShadowBody[]): void {
    if (bodies === boundTo) return;
    boundTo = bodies;
    receivers.length = 0;
    const byId = new Map(bodies.map((b) => [b.row.id, b]));
    for (const [id, ref] of refs) {
      const body = byId.get(id);
      if (!body) continue;
      const casters = casterRows.get(id)!
        .map((r) => byId.get(r.id))
        .filter((c): c is BodyShadowBody => c !== undefined);
      receivers.push({ body, casters, ref });
    }
  }

  const inv = new THREE.Quaternion();
  const v = new THREE.Vector3();
  // The deepest casters so far, deepest first: filled in place so a frame allocates nothing.
  const pickedC: Array<BodyShadowBody | null> = new Array<BodyShadowBody | null>(BODY_SHADOW_MAX_CASTERS).fill(null);
  const pickedM: number[] = new Array<number>(BODY_SHADOW_MAX_CASTERS).fill(0);

  function update(bodies: readonly BodyShadowBody[]): void {
    bind(bodies);
    shared.uBsForce.value = debug.force === null ? -1 : debug.force;
    for (const r of receivers) {
      const R = r.body.row.radius_m;
      const p = r.body.world;
      let n = 0;
      for (const c of r.casters) {
        const cw = c.world;
        const dc = Math.hypot(cw.x, cw.y, cw.z);
        if (!(dc > 0)) continue;
        const ux = cw.x / dc, uy = cw.y / dc, uz = cw.z / dc;
        const rx = p.x - cw.x, ry = p.y - cw.y, rz = p.z - cw.z;
        const along = rx * ux + ry * uy + rz * uz;
        if (along <= -R) continue;
        const ex = rx - along * ux, ey = ry - along * uy, ez = rz - along * uz;
        const off = Math.hypot(ex, ey, ez);
        const Rc = c.row.radius_m;
        const penumbra = Rc + Math.max(0, along) * ((SUN_RADIUS_M + Rc) / dc);
        const margin = off - R - penumbra;
        if (margin >= 0) continue;
        if (n === BODY_SHADOW_MAX_CASTERS && margin >= pickedM[n - 1]!) continue;
        let k = n < BODY_SHADOW_MAX_CASTERS ? n++ : n - 1;
        for (; k > 0 && pickedM[k - 1]! > margin; k--) {
          pickedC[k] = pickedC[k - 1]!;
          pickedM[k] = pickedM[k - 1]!;
        }
        pickedC[k] = c;
        pickedM[k] = margin;
      }
      const u = r.ref.uniforms;
      u.uBsCount.value = n;
      r.ref.casterIds.length = 0;
      inv.copy(r.body.quaternion).invert();
      u.uBsSun.value.set(-p.x, -p.y, -p.z).applyQuaternion(inv);
      for (let i = 0; i < n; i++) {
        const c = pickedC[i]!;
        const cw = c.world;
        v.set(cw.x - p.x, cw.y - p.y, cw.z - p.z);
        if (debug.offsetRadii !== 0) {
          // Across the Sun line, so the shadow moves on the disc rather than along its own axis.
          const dc = Math.hypot(cw.x, cw.y, cw.z);
          const nx = -cw.y / dc, ny = cw.x / dc;
          const nl = Math.hypot(nx, ny) || 1;
          const k = (debug.offsetRadii * c.row.radius_m) / nl;
          v.x += nx * k;
          v.y += ny * k;
        }
        v.applyQuaternion(inv);
        u.uBsCasters.value[i]!.set(v.x, v.y, v.z, c.row.radius_m);
        r.ref.casterIds.push(c.row.id);
      }
    }
  }

  return {
    refFor: (id) => refs.get(id) ?? null,
    update,
    setEyeShare(ground: number, weight: number): void {
      shared.uBsEye.value.set(Math.max(CORONA_SHARE, Math.min(1, ground)), Math.min(1, Math.max(0, weight)));
    },
    debug,
    dispose(): void {
      lut.dispose();
    },
  };
}
